import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  DetailList,
  Drawer,
  Heading,
  SegmentedControl,
  SegmentedControlItem,
  FormattedDate,
} from '@ideon-api/ideon-ui'
import type { AuthDuplicateCandidate, AuthDuplicateCandidateAccountRef } from '@ideon-api/sdk-internal'
import { sdkErrorMessage, useAuthServiceClient } from '../../lib/sdk-client'
import { useAccountEntitlements } from '../../lib/use-account-entitlements'
import { ConfirmMergeDialog } from './ConfirmMergeDialog'

// Re-exported under the names this file already used pre-migration so
// `DuplicateAccountsPage.tsx` and `ConfirmMergeDialog.tsx` don't need to
// change their imports -- only the underlying shape (now the SDK's camelCase
// types) changes.
export type DuplicateAccountSummary = AuthDuplicateCandidateAccountRef
export type DuplicateCandidate = AuthDuplicateCandidate

export const SIGNAL_LABEL: Record<DuplicateCandidate['kind'], string> = {
  near_name: 'Name match',
  identifier_conflict: 'SFDC conflict',
}

interface AccountSectionProps {
  account: DuplicateAccountSummary
  isSurvivor: boolean
  // keep=false means archive this one, so the other account survives.
  onChoose: (keep: boolean) => void
}

export function entitlementsValue(state: ReturnType<typeof useAccountEntitlements>): string {
  switch (state.status) {
    case 'loading':
      return 'Checking…'
    case 'error':
      return 'Unavailable'
    case 'ready': {
      if (!state.data.hasLiveEntitlements) return 'None'
      const { planCount, featureNames } = state.data
      const parts = []
      if (planCount > 0) parts.push(`${planCount} plan${planCount === 1 ? '' : 's'}`)
      if (featureNames.length > 0) parts.push(`${featureNames.length} feature${featureNames.length === 1 ? '' : 's'}`)
      return `Live — ${parts.join(', ')}`
    }
    case 'idle':
      // Unreachable today (AccountSection always passes a non-empty slug), but
      // it must never fall through to 'None' -- "no fetch happened" is not
      // evidence of "no entitlements", and this row drives an archive decision.
      return '—'
  }
}

function AccountSection({ account, isSurvivor, onChoose }: AccountSectionProps) {
  const entitlementsState = useAccountEntitlements(account.slug)

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Heading variant="title-m">{account.name}</Heading>
        <SegmentedControl
          aria-label={`Keep or archive ${account.name}`}
          value={isSurvivor ? 'keep' : 'archive'}
          onValueChange={(choice) => onChoose(choice === 'keep')}
        >
          <SegmentedControlItem value="keep">Keep</SegmentedControlItem>
          <SegmentedControlItem value="archive">Archive</SegmentedControlItem>
        </SegmentedControl>
      </div>
      <DetailList.Root>
        <DetailList.Item label="Slug" value={account.slug} data-testid={`duplicate-drawer-${account.id}-slug`} />
        <DetailList.Item
          label="SFDC ID"
          value={account.sfdcId ?? '—'}
          data-testid={`duplicate-drawer-${account.id}-sfdc-id`}
        />
        <DetailList.Item
          label="Created"
          value={<FormattedDate value={account.createdAt} />}
          data-testid={`duplicate-drawer-${account.id}-created`}
        />
        <DetailList.Item
          label="Entitlements"
          value={entitlementsValue(entitlementsState)}
          data-testid={`duplicate-drawer-${account.id}-entitlements`}
        />
      </DetailList.Root>
    </div>
  )
}

interface ReviewDuplicateDrawerProps {
  candidate: DuplicateCandidate | null
  onClose: () => void
  onDismissed: (candidateId: string) => void
  onMerged: (candidateId: string) => void
}

// Survivor picker (which account is "Keep" vs. "Archive") is client-side-only
// state -- it doesn't call the API until "Merge accounts" is confirmed.
export function ReviewDuplicateDrawer({ candidate, onClose, onDismissed, onMerged }: ReviewDuplicateDrawerProps) {
  const getClient = useAuthServiceClient()
  const [survivorSlug, setSurvivorSlug] = useState<string | null>(null)
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false)
  const [dismissing, setDismissing] = useState(false)
  const [dismissError, setDismissError] = useState<string | null>(null)
  // Keeps the last-shown candidate around while the drawer's close animation
  // plays -- Drawer.Root only unmounts its children ~300ms after isOpen
  // flips false, and `candidate` itself goes null immediately on close.
  const [displayCandidate, setDisplayCandidate] = useState<DuplicateCandidate | null>(null)

  useEffect(() => {
    if (candidate) {
      setDisplayCandidate(candidate)
      setSurvivorSlug(candidate.account.slug)
      setDismissError(null)
    }
  }, [candidate])

  const handleDismiss = async () => {
    if (!candidate) return
    setDismissing(true)
    setDismissError(null)
    try {
      const client = await getClient()
      await client.auth.dismissDuplicateCandidate(candidate.id)
      onDismissed(candidate.id)
      onClose()
    } catch (error) {
      setDismissError(sdkErrorMessage(error, 'Failed to dismiss.'))
    } finally {
      setDismissing(false)
    }
  }

  const survivor =
    displayCandidate && displayCandidate.account.slug === survivorSlug
      ? displayCandidate.account
      : displayCandidate?.duplicateAccount
  const loser =
    displayCandidate && displayCandidate.account.slug === survivorSlug
      ? displayCandidate.duplicateAccount
      : displayCandidate?.account

  return (
    <>
      <Drawer.Root isOpen={candidate != null} onClose={onClose} size="lg" testIdPrefix="review-duplicate-drawer">
        <Drawer.Header className="mb-4 border-b border-ideon-gray-40 pb-4">
          <Drawer.Title className="text-lg leading-7">Review duplicate</Drawer.Title>
          <Drawer.CloseButton />
        </Drawer.Header>
        {displayCandidate && (
          <>
            <Drawer.Body className="flex flex-col gap-4">
              <Alert variant="warning">
                Flagged as a likely duplicate -- {SIGNAL_LABEL[displayCandidate.kind]}
                {displayCandidate.confidence != null &&
                  ` (${Math.round(displayCandidate.confidence * 100)}% confidence)`}
                .
              </Alert>

              <AccountSection
                account={displayCandidate.account}
                isSurvivor={displayCandidate.account.slug === survivorSlug}
                onChoose={(keep) => setSurvivorSlug((keep ? displayCandidate.account : displayCandidate.duplicateAccount).slug)}
              />

              <div className="border-t border-ideon-gray-30" />

              <AccountSection
                account={displayCandidate.duplicateAccount}
                isSurvivor={displayCandidate.duplicateAccount.slug === survivorSlug}
                onChoose={(keep) => setSurvivorSlug((keep ? displayCandidate.duplicateAccount : displayCandidate.account).slug)}
              />

              {dismissError && <Alert variant="error">{dismissError}</Alert>}
            </Drawer.Body>
            <Drawer.Footer className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleDismiss} disabled={dismissing}>
                Not a duplicate
              </Button>
              <Button variant="filled" onClick={() => setMergeDialogOpen(true)} disabled={dismissing}>
                Merge accounts
              </Button>
            </Drawer.Footer>
          </>
        )}
      </Drawer.Root>

      {displayCandidate && survivor && loser && (
        <ConfirmMergeDialog
          isOpen={mergeDialogOpen}
          onClose={() => setMergeDialogOpen(false)}
          survivor={survivor}
          loser={loser}
          onMerged={() => {
            setMergeDialogOpen(false)
            onMerged(displayCandidate.id)
            onClose()
          }}
        />
      )}
    </>
  )
}
